/**
 * Guards against the "NotFoundError: The object can not be found here" crash
 * (German Safari: "Das Objekt ist hier nicht zu finden") that React throws when
 * a browser/OS auto-translator (iOS Safari Translate, Google Translate, etc.)
 * mutates the DOM out from under React.
 *
 * The translator wraps/relocates text nodes in injected <font>/<span> elements.
 * When React later calls removeChild/insertBefore on a node that has been moved,
 * the node is no longer a child of the expected parent and the browser throws a
 * fatal NotFoundError, surfacing as a full-screen "Unexpected application error".
 *
 * This patch makes both operations no-op (instead of throwing) when the target
 * is not actually under the expected parent. Translation keeps working; the app
 * no longer crashes. This is the React team's documented workaround:
 * https://github.com/facebook/react/issues/11538#issuecomment-417504600
 *
 * Imported first in index.jsx so it runs before React renders anything.
 */
if (typeof Node === 'function' && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (child) {
    if (child.parentNode !== this) {
      if (typeof console !== 'undefined') {
        console.warn('Suppressed removeChild on a translated/relocated node', child, this);
      }
      return child;
    }
    // eslint-disable-next-line prefer-rest-params
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (typeof console !== 'undefined') {
        console.warn('Suppressed insertBefore against a translated/relocated reference node', referenceNode, this);
      }
      return newNode;
    }
    // eslint-disable-next-line prefer-rest-params
    return originalInsertBefore.apply(this, arguments);
  };
}
